<template>
  <div class="select-site-page">
    <CommonOverlay :show="isLoading" title="Loading Site" message="Please wait while we load the site details..." style="z-index: 10000; position: fixed; top: 0; left: 0;" />
    <b-container>
      <h5 class="logo">
        <i class="fa fa-circle text-warning" />
          <img src="../../assets/teamio-logo.png" alt="TeamIO" width="100" />
        <i class="fa fa-circle text-warning" />
      </h5>
      <ErrorDisplay/>
      <Widget class="mx-auto loginpage-widget">
        <div class="select-site-container">
          <h4 class="select-site-title">Select Site</h4>
          <p class="select-site-description">Your account has access to multiple sites. Please select which site you'd like to log into:</p>
          
          <div v-if="loadError" class="error-message">
            <p>{{ loadError }}</p>
            <b-button variant="outline-secondary" @click="goBackToLogin">Back to Login</b-button>
          </div>
          
          <div v-else>
            <b-form-group>
              <b-form-select
                v-model="selectedSite"
                :options="siteOptions"
                aria-label="Select a site to log into"
                class="site-select"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>-- Please select a site --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
            
            <b-button 
              variant="primary" 
              class="select-site-button" 
              @click="continueToSite"
              :disabled="!selectedSite"
            >
              Continue
            </b-button>
          </div>
        </div>
      </Widget>
    </b-container>
    <footer class="footer">
      2025 &copy; Teamio.app
    </footer>
  </div>
</template>

<script>
import Widget from '@/components/Widget/Widget';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CommonOverlay from "@/components/CommonOverlay";
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import _ from 'lodash';
import LogRocket from 'logrocket';
import { pickSiteList, upsertSiteRow, resolveSwitchUser, switchErrorMessage } from '@/utils/siteSwitch';

export default {
  name: 'SelectSitePage',
  components: { Widget, ErrorDisplay, CommonOverlay },
  
  data() {
    return {
      selectedSite: null,
      userSites: [],
      loadError: null,
      isLoading: false
    };
  },

  created() {
    try {
      const sitesData = localStorage.getItem('userSites');
      if (!sitesData) {
        this.loadError = 'No site data found. Please try logging in again.';
        return;
      }
      
      this.userSites = JSON.parse(sitesData);
      
      if (!this.userSites || !Array.isArray(this.userSites) || this.userSites.length === 0) {
        this.loadError = 'Invalid site data. Please try logging in again.';
        return;
      }
    } catch (error) {
      console.error('Error loading site data:', error);
      this.loadError = 'Error loading site data. Please try logging in again.';
      return;
    }
    // Login is already signed in to Firebase here, so ask the server for the full list (a Teamio
    // operator sees every active practice). Failure keeps the login list.
    this.$store.dispatch('customer/getSwitchableSites').then((serverSites) => {
      this.userSites = pickSiteList(serverSites, this.userSites);
    });
  },

  computed: {
    ...mapState({
      loading: state => state.alert.loading,
      errored: state => state.alert.errored,
      alertMessage: state => state.alert.message
    }),
    
    siteOptions() {
      return this.userSites.map(site => ({
        value: site,
        text: site.SiteName || 'Unknown Site'
      }));
    }
  },

  methods: {
    ...mapActions('alert', ['setErrored', 'setMessage']),
    ...mapActions('customer', ['getSitesAndEmployeesAndStatusesAndListsAndSettingsAndPatientsAndProviders']),
    ...mapMutations('customer', ['setRules']),

    goBackToLogin() {
      localStorage.removeItem('userSites');
      this.$router.push('/login');
    },

    async continueToSite() {
      if (!this.selectedSite) {
        this.setErrored(true);
        this.setMessage('Please select a site to continue');
        return;
      }

      try {
        this.isLoading = true; // Show overlay during loading
        // Process the selected site data similar to the original login flow
        const site = this.selectedSite;
        
        if (!site) {
          this.setErrored(true);
          this.setMessage('No site selected. Please select a site to continue.');
          return;
        }

        // The server sets the session for the site (creating a Teamio operator's row there if
        // needed) and returns the row to log in with — the picked entry may be a stub.
        const result = await this.$store.dispatch('customer/selectSite', { SiteID: site.SiteID, EmployeeID: site.EmployeeID });
        const user = resolveSwitchUser(result, site);
        if (!user) {
          this.setErrored(true);
          this.setMessage(switchErrorMessage(result) || 'Error loading site. Please try again.');
          return;
        }
        localStorage.setItem('userSites', JSON.stringify(upsertSiteRow(this.userSites, user)));
        await this.$store.dispatch('customer/handleSelectSite', user);
        
        
      } catch (error) {
        console.error('Error continuing to site:', error);
        this.setErrored(true);
        this.setMessage('Error loading site. Please try again.');
      } finally {
        this.isLoading = false; // Hide overlay after operation
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.select-site-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fa;
  
  .loginpage-widget {
    width: 100%;
    max-width: 450px;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border-radius: 0.5rem;
    padding: 2rem;
    background-color: white;
    margin-left: auto;
    margin-right: auto;
  }

  .logo {
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    text-align: center;
    
    i {
      margin: 0 0.5rem;
    }
  }

  .select-site-container {
    text-align: center;
    padding: 1rem 0;
  }

  .select-site-title {
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
  }

  .select-site-description {
    margin-bottom: 1.5rem;
    color: #666;
  }

  .site-select {
    margin-bottom: 1.5rem;
    font-size: 1rem;
  }

  .select-site-button {
    width: 100%;
    padding: 0.75rem;
    font-weight: 600;
    border-radius: 0.25rem;
  }

  .error-message {
    color: #dc3545;
    margin-bottom: 1.5rem;
    
    p {
      margin-bottom: 1rem;
    }
  }

  .footer {
    margin-top: 2rem;
    color: #777;
    font-size: 0.875rem;
  }
}
</style> 